import { Box, IconButtonProps, Link, styled, Typography } from '@mui/material';
import { GridColDef, GridRowId } from '@mui/x-data-grid-premium';
import { BaseCampaignItem, EditableItemField } from '@retail/campaign/types';
import { StackedTextCell } from '@retail/components';
import { EditButtonIcon } from '@shared/custom-icons';
import dayjs from 'dayjs';
import React, { useCallback, useState } from 'react';
import { useAppTFunction } from '@retail/app/i18n';
import { PricePerUnit, useMgItemAndExternalNumberColumn } from '@retail/products/components';
import { IconButtonWithTooltip, SelectInputField } from '@shared/components';
import { ConfirmEditButtons } from '@retail/my-assortment/components';
import { useController, useForm } from 'react-hook-form';
import {
  purchasePeriodSchema,
  PurchasePeriodSchema,
  purchasePriceSchema,
  PurchasePriceSchema,
  salesPriceSchema,
  SalesPriceSchema,
} from '../editCampaignSchema';
import { CampaignItemActions } from '../CampaignItemActions';
import { yupResolver } from '@hookform/resolvers/yup';
import { EditPriceCell } from '../EditPriceCell';
import { DateRangePicker } from '@mui/x-date-pickers-pro';

interface Props<T extends BaseCampaignItem> {
  getEditableFields: (item: T) => EditableItemField[];
  includeVat: boolean;
  deleteItem?: (campaignItem: T) => void;
  setSelectedItem: (item: T | null) => void;
  handleSubmitSalesPrice: (item: T, salesPrice: number) => Promise<void>;
  handleSubmitPurchasePrice: (item: T, salesPrice: number) => Promise<void>;
  handleSubmitPurchasePeriod: (item: T, from?: string, to?: string) => Promise<void>;
  handleSubmitCampaignUnit: (item: T, campaignUnit: string) => Promise<void>;
  externalSourceType: 'NOBB' | 'FINFO';
}

export const baseColumnProps: Partial<GridColDef> = {
  width: 225,
  align: 'left',
  headerAlign: 'left',
  headerName: '',
  editable: false,
  disableColumnMenu: true,
  sortable: false,
};

type EditableField = 'campaignUnit' | 'salesPrice' | 'purchasePrice' | 'purchasePeriod';

interface ItemCell {
  id: GridRowId;
  field: EditableField;
}

export const useGetCampaignItemsBaseColumns = <T extends BaseCampaignItem>({
  getEditableFields,
  includeVat,
  deleteItem,
  setSelectedItem,
  handleSubmitSalesPrice,
  handleSubmitPurchasePrice,
  handleSubmitPurchasePeriod,
  handleSubmitCampaignUnit,
  externalSourceType,
}: Props<T>) => {
  const t = useAppTFunction();
  const [editingCell, setEditingCell] = useState<ItemCell>();

  const salesPriceFormMethods = useForm<SalesPriceSchema>({
    resolver: yupResolver(salesPriceSchema(t)),
  });
  const purchasePriceFormMethods = useForm<PurchasePriceSchema>({
    resolver: yupResolver(purchasePriceSchema(t)),
  });
  const purchasePeriodFormMethods = useForm<PurchasePeriodSchema>({
    resolver: yupResolver(purchasePeriodSchema(t)),
    mode: 'all',
  });

  const salesPriceController = useController({
    name: 'salesPrice',
    control: salesPriceFormMethods.control,
  });
  const purchasePriceController = useController({
    name: 'purchasePrice',
    control: purchasePriceFormMethods.control,
  });
  const purchasePeriodFromController = useController({
    name: 'purchasePeriodFrom',
    control: purchasePeriodFormMethods.control,
  });
  const purchasePeriodToController = useController({
    name: 'purchasePeriodTo',
    control: purchasePeriodFormMethods.control,
  });

  const onSubmitSalesPrice = useCallback(
    (item: T) =>
      salesPriceFormMethods.handleSubmit(async ({ salesPrice }) => {
        await handleSubmitSalesPrice(item, salesPrice);
        setEditingCell(undefined);
        salesPriceFormMethods.reset();
      }, console.error),
    [handleSubmitSalesPrice, salesPriceFormMethods]
  );
  const onSubmitPurchasePrice = useCallback(
    (item: T) =>
      purchasePriceFormMethods.handleSubmit(async ({ purchasePrice }) => {
        await handleSubmitPurchasePrice(item, purchasePrice);
        setEditingCell(undefined);
        purchasePriceFormMethods.reset();
      }, console.error),
    [handleSubmitPurchasePrice, purchasePriceFormMethods]
  );
  const onSubmitCampaignUnit = useCallback(
    async (item: T, campaignUnit: string) => {
      await handleSubmitCampaignUnit(item, campaignUnit);
      setEditingCell(undefined);
    },
    [handleSubmitCampaignUnit]
  );
  const onSubmitPurchasePeriod = useCallback(
    async (item: T) => {
      const handleSubmit = purchasePeriodFormMethods.handleSubmit(
        async ({ purchasePeriodFrom, purchasePeriodTo }) => {
          await handleSubmitPurchasePeriod(
            item,
            purchasePeriodFrom ? dayjs(purchasePeriodFrom).format('YYYY-MM-DD') : undefined,
            purchasePeriodTo ? dayjs(purchasePeriodTo).format('YYYY-MM-DD') : undefined
          );
          setEditingCell(undefined);

          purchasePeriodFormMethods.reset();
        },
        console.error
      );
      return handleSubmit();
    },
    [handleSubmitPurchasePeriod, purchasePeriodFormMethods]
  );

  const primaryTextColumn: GridColDef<T> = {
    ...baseColumnProps,
    field: 'primaryText',
    width: 300,
    headerName: t('products.mgItem.supplierItem.primaryText'),
    renderCell: ({ row }) => (
      <StackedTextCell
        topLine={
          <Link
            onClick={() => setSelectedItem(row)}
            sx={{
              cursor: 'pointer',
              color: 'inherit',
            }}
          >
            <Typography fontWeight="600" variant="body2">
              {row.primaryText}
            </Typography>
          </Link>
        }
        bottomLine={row.secondaryText}
        nullableBottomLine
        slotProps={{ topLine: { maxLines: 2 }, bottomLine: { maxLines: 1 } }}
      />
    ),
  };

  const mgItemAndExternalNumberColumn = useMgItemAndExternalNumberColumn<T>({
    externalItemSource: externalSourceType,
  });

  const supplierColumn: GridColDef<T> = {
    ...baseColumnProps,
    field: 'supplier',
    width: 200,
    headerName: t('products.supplier.name'),
    renderCell: ({ row }) => (
      <StackedTextCell topLine={row.mgSupplierName} bottomLine={row.mgSupplierId} />
    ),
  };

  const primaryPackageUnitColumn: GridColDef<T> = {
    ...baseColumnProps,
    field: 'primaryPackageUnit',
    headerAlign: 'center',
    align: 'center',
    width: 140,
    headerName: t('campaign.columns.primaryPackageUnit'),
    renderCell: ({ row }) => (
      <Typography variant="subtitle2" component="span">
        {row.primaryPackageUnit}
      </Typography>
    ),
  };

  const campaignUnitColumn: GridColDef<T> = {
    ...baseColumnProps,
    field: 'campaignUnit',
    headerAlign: 'center',
    align: 'center',
    width: 140,
    headerName: t('campaign.columns.campaignUnit.label'),
    renderCell: ({ id, row }) => {
      if (editingCell?.id === id && editingCell?.field === 'campaignUnit') {
        let campaignUnit = row.campaignUnit;

        const units = row.campaignSalesUnitPrices?.map((price) => ({
          value: price.unit.unit,
          name: price.unit.unit,
        }));

        if (units && units?.length > 0) {
          return (
            <Box display="flex" px={2}>
              <CellSelectInputField
                size="small"
                menuItems={units}
                minWidth={100}
                defaultValue={row.campaignUnit}
                onChange={(e) => {
                  campaignUnit = e.target.value as string;
                }}
              />

              <ConfirmEditButtons
                onSubmit={() => onSubmitCampaignUnit(row, campaignUnit)}
                onCancel={() => setEditingCell(undefined)}
              />
            </Box>
          );
        }

        return row.campaignPurchaseUnitPrices?.[0].unit.unit;
      }
      return (
        <Box display="flex" alignItems="center">
          <Typography variant="subtitle2" component="span">
            {row.campaignUnit}
          </Typography>

          {getEditableFields(row).includes('campaignUnit') && (
            <EditIconButtonWithTooltip
              title={`${t('campaign.columns.campaignUnit.edit')} ${row.primaryText}`}
              onClick={() => {
                setEditingCell({ id, field: 'campaignUnit' });
              }}
            />
          )}
        </Box>
      );
    },
  };

  const campaignSalesPriceColumn: GridColDef<T> = {
    ...baseColumnProps,
    width: 200,
    field: 'campaignSalesPrice',
    headerName: t('campaign.columns.campaignSalesPrice.label'),
    renderCell: ({ id, row }) => {
      const salesPrice = row.getSalesPriceForCampaignUnit();

      if (id === editingCell?.id && editingCell?.field === 'salesPrice') {
        const onCancel = () => {
          setEditingCell(undefined);
          salesPriceFormMethods.reset();
        };
        return (
          <StackedTextCell
            topLine={
              <EditPriceCell
                onSubmit={onSubmitSalesPrice(row)}
                onCancel={onCancel}
                controller={salesPriceController}
                initValue={includeVat ? salesPrice?.priceWithVat : salesPrice?.priceWithoutVat}
              />
            }
            bottomLine={t('campaign.columns.incVat')}
          />
        );
      }

      return (
        <StackedTextCell
          topLine={
            <Box display="flex" alignItems="center">
              <PricePerUnit
                price={salesPrice?.getLocaleFormattedPrice(includeVat) ?? '-'}
                unit={row.campaignUnit}
              />
              {getEditableFields(row).includes('salesPrice') && (
                <EditIconButtonWithTooltip
                  title={`${t('campaign.columns.campaignSalesPrice.edit')} ${row.primaryText}`}
                  onClick={() => {
                    setEditingCell({ id, field: 'salesPrice' });
                  }}
                />
              )}
            </Box>
          }
          bottomLine={
            salesPrice
              ? includeVat
                ? t('campaign.columns.incVat')
                : t('campaign.columns.exVat')
              : undefined
          }
          gap="small"
        />
      );
    },
  };

  const campaignPurchasePriceColumn: GridColDef<T> = {
    ...baseColumnProps,
    field: 'campaignPurchasePrice',
    width: 200,
    headerName: t('campaign.columns.campaignPurchasePrice.label'),
    renderCell: ({ row, id }) => {
      const purchasePrice = row.getPurchasePriceForCampaignUnit();
      if (editingCell?.id === id && editingCell?.field === 'purchasePrice') {
        const onCancel = () => {
          setEditingCell(undefined);
          purchasePriceFormMethods.reset();
        };
        return (
          <StackedTextCell
            topLine={
              <EditPriceCell
                onSubmit={onSubmitPurchasePrice(row)}
                onCancel={onCancel}
                controller={purchasePriceController}
                initValue={purchasePrice?.priceWithoutVat}
              />
            }
            bottomLine={t('campaign.columns.exVat')}
          />
        );
      }

      return (
        <StackedTextCell
          topLine={
            <Box display="flex" alignItems="center">
              <PricePerUnit
                price={purchasePrice?.getLocaleFormattedPriceExVat() ?? '-'}
                unit={row.campaignUnit}
              />
              {getEditableFields(row).includes('purchasePrice') && (
                <EditIconButtonWithTooltip
                  title={`${t('campaign.columns.campaignPurchasePrice.edit')} ${row.primaryText}`}
                  onClick={() => {
                    setEditingCell({ id, field: 'purchasePrice' });
                  }}
                />
              )}
            </Box>
          }
          bottomLine={purchasePrice ? t('campaign.columns.exVat') : undefined}
          gap="small"
        />
      );
    },
  };

  const campaignSalesCoverageColumn: GridColDef<T> = {
    ...baseColumnProps,
    field: 'campaignSalesCoverage',
    width: 120,
    headerName: t('campaign.columns.campaignSalesCoverage'),
    renderCell: ({ row }) => {
      const campaignSalesCoverage = row.getFormattedCampaignSalesCoverage();

      return campaignSalesCoverage ? (
        <StackedTextCell
          topLine={campaignSalesCoverage}
          bottomLine={t('campaign.columns.exFreight')}
          gap="small"
        />
      ) : (
        '-'
      );
    },
  };

  const purchasePeriodColumnn: GridColDef<T> = {
    ...baseColumnProps,
    field: 'purchasePeriod',
    minWidth: 320,
    headerName: `${t('campaign.columns.purchasePeriodFrom')}/${t(
      'campaign.columns.purchasePeriodTo'
    )}`,
    renderCell: ({ row, id }) => {
      if (editingCell?.id === id && editingCell?.field === 'purchasePeriod') {
        const onCancel = () => {
          setEditingCell(undefined);
          purchasePeriodFormMethods.reset();
        };

        const onSubmit = () => onSubmitPurchasePeriod(row);

        return (
          <Box display="flex" alignItems="center" gap={1}>
            <DateRangePicker
              value={[
                purchasePeriodFromController.field.value,
                purchasePeriodToController.field.value,
              ]}
              onChange={([from, to]) => {
                purchasePeriodFromController.field.onChange(from);
                purchasePeriodToController.field.onChange(to);
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    minWidth: 150,
                    '& .MuiPickersInputBase-root': {
                      fontSize: '14px',
                    },
                  },
                  error:
                    !!purchasePeriodFormMethods.formState.errors.purchasePeriodFrom ||
                    !!purchasePeriodFormMethods.formState.errors.purchasePeriodTo,
                  helperText:
                    purchasePeriodFormMethods.formState.errors.purchasePeriodFrom?.message ||
                    purchasePeriodFormMethods.formState.errors.purchasePeriodTo?.message,
                },
              }}
              formatDensity="dense"
            />
            <ConfirmEditButtons
              disableOnSubmit={
                !!purchasePeriodFormMethods.formState.errors.purchasePeriodFrom ||
                !!purchasePeriodFormMethods.formState.errors.purchasePeriodTo
              }
              onSubmit={onSubmit}
              onCancel={onCancel}
            />
          </Box>
        );
      }

      const validFrom = row.purchasePeriodFrom ? dayjs(row.purchasePeriodFrom).format('ll') : '-';
      const validTo = row.purchasePeriodTo ? dayjs(row.purchasePeriodTo).format('ll') : '-';

      return (
        <Box display="flex">
          <StackedTextCell topLine={validFrom} bottomLine={validTo} />
          {getEditableFields(row).includes('purchasePeriod') && (
            <EditIconButtonWithTooltip
              title={`${t('campaign.columns.purchasePeriod.edit')} ${row.primaryText}`}
              onClick={() => {
                setEditingCell({ id, field: 'purchasePeriod' });
              }}
            />
          )}
        </Box>
      );
    },
  };

  const createdByColumn: GridColDef<T> = {
    ...baseColumnProps,
    width: 200,
    field: 'createdBy',
    headerName: t('campaign.columns.createdBy'),
    renderCell: ({ row }) => (
      <Typography variant="subtitle2" component="span">
        {row.createdBy ? `${row.createdBy.givenName} ${row.createdBy.familyName}` : '-'}
      </Typography>
    ),
  };

  const actionsColumn: GridColDef<T> = {
    ...baseColumnProps,
    field: 'actions',
    headerName: '',
    width: 55,
    renderCell: ({ row }) => (
      <CampaignItemActions removeItem={deleteItem ? () => deleteItem(row) : undefined} />
    ),
  };

  return {
    primaryTextColumn,
    mgItemAndExternalNumberColumn,
    supplierColumn,
    primaryPackageUnitColumn,
    campaignUnitColumn,
    campaignSalesPriceColumn,
    campaignPurchasePriceColumn,
    purchasePeriodColumnn,
    campaignSalesCoverageColumn,
    createdByColumn,
    actionsColumn,
  };
};

export const CellSelectInputField = styled(SelectInputField)(({ theme }) => ({
  '&.MuiOutlinedInput-root': {
    height: 32,
    border: `1 px solid ${theme.vars.palette.divider}`,
    marginTop: '-4px',
  },
}));

export const EditIconButtonWithTooltip: React.FC<IconButtonProps> = (props) => (
  <IconButtonWithTooltip {...props}>
    <EditButtonIcon fontSize="small" />
  </IconButtonWithTooltip>
);

export type CampaignItemsBaseColumns<T extends BaseCampaignItem> = ReturnType<
  typeof useGetCampaignItemsBaseColumns<T>
>;
